import * as React from 'react';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { AlertDialog } from '../composites/AlertDialog';
import type { AlertDialogVariant } from '../composites/AlertDialog';
import { wealthlaneAlert } from './niceModal';

export interface ConfirmDialogProps {
  title?: string;
  description?: string;
  actionLabel?: string;
  cancelLabel?: string;

  variant?: AlertDialogVariant;
  subject?: string;
  /** When true, Title and Description auto-generate from variant + subject */
  preset?: boolean;

  remarksRequired?: boolean;
  remarksLabel?: string;
  defaultRemarks?: string;

  /** Called when user clicks Action. Receives remarks when remarksRequired. */
  onAction?: (remarks?: string) => void | Promise<void>;

  semanticId?: string;
}

export interface ConfirmDialogResult {
  confirmed: boolean;
  remarks?: string;
}

export const ConfirmDialog = NiceModal.create((props: ConfirmDialogProps) => {
  const modal = useModal();
  const [loading, setLoading] = React.useState(false);
  const [remarks, setRemarks] = React.useState(props.defaultRemarks ?? '');

  const handleCancel = () => {
    modal.resolve({ confirmed: false } satisfies ConfirmDialogResult);
    void modal.hide();
  };

  const handleAction = async () => {
    if (props.onAction) {
      setLoading(true);
      try {
        await props.onAction(props.remarksRequired ? remarks : undefined);
      } finally {
        setLoading(false);
      }
    }
    modal.resolve({
      confirmed: true,
      remarks: props.remarksRequired ? remarks : undefined,
    } satisfies ConfirmDialogResult);
    void modal.hide();
  };

  const isSingleButton = props.variant === 'single' || props.variant === 'viewRemarks';

  return (
    <AlertDialog.Root
      {...wealthlaneAlert(modal)}
      variant={props.variant}
      subject={props.subject}
      remarksRequired={props.remarksRequired}
      remarks={remarks}
      onRemarksChange={setRemarks}
    >
      <AlertDialog.Portal>
        <AlertDialog.Overlay />
        <AlertDialog.Content semanticId={props.semanticId}>
          <AlertDialog.Title preset={props.preset}>
            {props.title}
          </AlertDialog.Title>

          {(props.description || props.preset) && (
            <AlertDialog.Description preset={props.preset}>
              {props.description}
            </AlertDialog.Description>
          )}

          {props.remarksRequired && (
            <AlertDialog.Remarks label={props.remarksLabel} />
          )}

          {props.variant === 'viewRemarks' && props.defaultRemarks && (
            <AlertDialog.Remarks readOnly label={props.remarksLabel} />
          )}

          <AlertDialog.Footer>
            {!isSingleButton && (
              <AlertDialog.Cancel onClick={handleCancel}>
                {props.cancelLabel}
              </AlertDialog.Cancel>
            )}
            <AlertDialog.Action
              onClick={handleAction}
              closeOnClick={false}
              loading={loading}
            >
              {props.actionLabel}
            </AlertDialog.Action>
          </AlertDialog.Footer>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
});
